import { googleJson, withQuery } from "./request";
import { logFileUpload } from "./Logging";

export enum GoogleOperationStatus {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
};

type DrivePermission = {
  id?: string;
  type?: string;
  role?: string;
  emailAddress?: string;
};

const DRIVE_API = "https://www.googleapis.com/drive/v3";
const PERMISSION_FIELDS = "permissions(id,type,role,emailAddress)";

export default class GoogleClient {
  static AddedContentSharedDriveId: string = "0AChrdAv4MJNZUk9PVA";
  static ParentalContentSharedDriveId: string = "0AJlTgzz2jZ2gUk9PVA";
  static PBEEJApiServiceAccountEmail: string = "workspace-account@pbeej-343804.iam.gserviceaccount.com";

  static async addCollaborators(fileId: string, collaboratorsEmails: string[]): Promise<void> {
    const metadata = { operation: 'GoogleClient.addCollaborators', fileId, collaboratorsEmails };
    await Promise.all(collaboratorsEmails.map((email) =>
      googleJson(withQuery(`${DRIVE_API}/files/${encodeURIComponent(fileId)}/permissions`, {
        emailMessage: "You've been added as a collaborator on a PBEEJ document!",
        sendNotificationEmail: true,
        supportsAllDrives: true,
      }), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emailAddress: email,
          role: "writer",
          type: "user",
        }),
      })
    ))
    .then((res) => { this.logCompletion(metadata); return res; })
    .catch((err) => { this.logFailure(metadata, err); throw err; });
  };

  static async addServiceAccountAsCollaborator(fileId: string): Promise<void> {
    const metadata = { operation: 'GoogleClient.addServiceAccountAsCollaborator', fileId };
    await GoogleClient.addCollaborators(fileId, [this.PBEEJApiServiceAccountEmail])
    .then((res) => { this.logCompletion(metadata); return res; })
    .catch((err) => { this.logFailure(metadata, err); throw err; });
  }

  static async removeAllCollaborators(fileId: string): Promise<any> {
    const collaboratorPermissions = await googleJson<{ permissions?: DrivePermission[] }>(
      withQuery(`${DRIVE_API}/files/${encodeURIComponent(fileId)}/permissions`, {
        supportsAllDrives: true,
        fields: PERMISSION_FIELDS,
      }),
    ).then((response) => {
      const allPermissions = response.permissions ?? [];
      return allPermissions.filter((permission) =>
        permission.type === 'user'
        && permission.role !== 'owner'
        && permission.emailAddress !== this.PBEEJApiServiceAccountEmail
      );
    });
    const metadata = { operation: 'GoogleClient.removeAllCollaborators', fileId, collaboratorPermissions };
    await Promise.all(collaboratorPermissions.map((permission) => googleJson(
      withQuery(`${DRIVE_API}/files/${encodeURIComponent(fileId)}/permissions/${encodeURIComponent(permission.id ?? "")}`, {
        supportsAllDrives: true,
      }),
      { method: "DELETE" },
    )))
    .then((res) => { this.logCompletion(metadata); return res; })
    .catch((err) => { this.logFailure(metadata, err); throw err; });
  }

  static async addDomainReadPermission(fileId: string): Promise<any> {
    const metadata = { operation: 'GoogleClient.addDomainReadPermission', fileId };
    return googleJson(withQuery(`${DRIVE_API}/files/${encodeURIComponent(fileId)}/permissions`, {
      supportsAllDrives: true,
    }), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        domain: "pbeej.com",
        role: "reader",
        type: "domain",
      }),
    })
    .then((res) => { this.logCompletion(metadata); return res; })
    .catch((err) => { this.logFailure(metadata, err); throw err; });
  }

  static async removeDomainReadPermission(fileId: string): Promise<any> {
    const metadata = { operation: 'GoogleClient.removeDomainReadPermission', fileId };
    const response = await googleJson<{ permissions?: DrivePermission[] }>(
      withQuery(`${DRIVE_API}/files/${encodeURIComponent(fileId)}/permissions`, {
        supportsAllDrives: true,
        fields: PERMISSION_FIELDS,
      }),
    );
    const domainPermission = response.permissions?.find(permission => permission.type === "domain");
    if (domainPermission) {
      return googleJson(
        withQuery(`${DRIVE_API}/files/${encodeURIComponent(fileId)}/permissions/${encodeURIComponent(domainPermission.id ?? "")}`, {
          supportsAllDrives: true,
        }),
        { method: "DELETE" },
      )
      .then((res) => { this.logCompletion(metadata); return res; })
      .catch((err) => { this.logFailure(metadata, err); throw err; });
    } else {
      this.logSkip(metadata, 'no domain read permission found to remove');
    }
  }

  static async isOnSharedDrive(fileId: string): Promise<boolean> {
    var fileResponse = await googleJson<{ driveId?: string }>(
      withQuery(`${DRIVE_API}/files/${encodeURIComponent(fileId)}`, {
        supportsAllDrives: true,
        fields: "driveId",
      }),
    );
    const { driveId } = fileResponse;
    return driveId?.length > 0;
  }

  static async moveToSharedDrive(fileId: string, isParentalContent = false, isParent = false): Promise<any> {
    const metadata = { operation: 'GoogleClient.moveToSharedDrive', fileId, isParentalContent, isParent };
    await googleJson(withQuery(`${DRIVE_API}/files/${encodeURIComponent(fileId)}`, {
      supportsAllDrives: true,
      // Note: addParents refers to adding "parent" folder (in this case, a Shared Drive) to a Google Drive item, whereas isParentalContent is a PBEEJ concept :)
      addParents: isParentalContent
        ? this.ParentalContentSharedDriveId
        : this.AddedContentSharedDriveId,
      // PBEEJ children don't have access to Parental Content drive and thus cannot remove it as the Shared Drive holding this file.
      // Of course, they could never have added a file to the Parental Content drive in the first place,
      // so all they can / need to do is move files from MyDrive to Added Content shared drive and keep them there,
      // making "removeParents" necessary / possible for parents only.
      ...(isParent && {
        removeParents: isParentalContent
          ? this.AddedContentSharedDriveId
          : this.ParentalContentSharedDriveId,
      }),
    }), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
    .then((res) => { this.logCompletion(metadata); return res; })
    .catch((err) => { this.logFailure(metadata, err); throw err; });
  }

  static async deleteMyDriveFile(fileId: string): Promise<any> {
    const metadata = { operation: 'GoogleClient.deleteMyDriveFile', fileId };
    return googleJson(`${DRIVE_API}/files/${encodeURIComponent(fileId)}`, {
      method: "DELETE",
    })
    .then((res) => { this.logCompletion(metadata); return res; })
    .catch((err) => { this.logFailure(metadata, err); throw err; });
  }

  static logCompletion(metadata: object) {
    // best effort; do not await
    logFileUpload({ status: GoogleOperationStatus.COMPLETED, ...metadata }).catch(console.error);
  }

  static logFailure(metadata: object, err: any) {
    // best effort; do not await
    logFileUpload({ status: GoogleOperationStatus.FAILED, error: err, ...metadata }).catch(console.error);
  }

  static logSkip(metadata: object, reason: string) {
    // best effort; do not await
    logFileUpload({ status: GoogleOperationStatus.SKIPPED, reason, ...metadata }).catch(console.error);
  }
};
